import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideLogOut, lucideMenu } from '@ng-icons/lucide';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { AuthService } from '../../../../services/auth.service';
import { Company } from '../../../../services/company.service';
import { companyInitials } from '../company-initials';
import { MAIN_NAV_LINKS } from '../nav-links';
import { SetupProgressComponent } from '../setup-progress/setup-progress.component';

/** Hamburger trigger + navigation sheet, replaces the hand-rolled mobile overlay. */
@Component({
  selector: 'app-mobile-nav',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgIcon,
    HlmAvatarImports,
    HlmButtonImports,
    HlmItemImports,
    HlmSeparatorImports,
    HlmSheetImports,
    SetupProgressComponent,
  ],
  providers: [provideIcons({ lucideLogOut, lucideMenu })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-sheet side="left" [state]="state()" (stateChanged)="onStateChange($event)">
      <button hlmSheetTrigger hlmBtn variant="ghost" size="icon" aria-label="Menü öffnen">
        <ng-icon name="lucideMenu" />
      </button>

      <hlm-sheet-content *hlmSheetPortal class="w-[88%] max-w-sm p-0" aria-label="Navigation">
        <div hlmSheetHeader>
          <h2 hlmSheetTitle>{{ company().name }}</h2>
          <p hlmSheetDescription>ID: {{ company().public_id }}</p>
        </div>

        <nav hlmItemGroup class="px-2">
          @for (link of links(); track link.route) {
            <a
              hlmBtn
              variant="ghost"
              class="w-full justify-start"
              [routerLink]="link.route"
              routerLinkActive="bg-accent text-accent-foreground"
              (click)="close()">
              {{ link.label }}
            </a>
          }
        </nav>

        <app-setup-progress bordered class="mx-4 mt-2" (navigated)="close()" />

        <div class="flex-1"></div>

        <div hlmSeparator></div>

        <div hlmItem class="rounded-none border-none">
          <div hlmItemMedia variant="image">
            <hlm-avatar>
              @if (logoUrl(); as url) {
                <img hlmAvatarImage [src]="url" [alt]="company().name" />
              }
              <span hlmAvatarFallback>{{ initials() }}</span>
            </hlm-avatar>
          </div>
          <div hlmItemContent>
            <span hlmItemTitle>{{ company().name }}</span>
          </div>
          <div hlmItemActions>
            <button hlmBtn variant="outline" size="sm" (click)="logout()">
              Logout
              <ng-icon name="lucideLogOut" data-icon="inline-end" />
            </button>
          </div>
        </div>
      </hlm-sheet-content>
    </hlm-sheet>
  `,
})
export class MobileNavComponent {
  private readonly authService = inject(AuthService);

  readonly company = input.required<Company>();
  readonly logoUrl = input<string | null>(null);

  /** Mirrors the sheet state so the layout can lock body scrolling. */
  readonly openChange = output<boolean>();

  protected readonly state = signal<'open' | 'closed'>('closed');

  protected readonly initials = computed(() => companyInitials(this.company().name));

  protected readonly links = computed(() =>
    MAIN_NAV_LINKS.filter((link) => !link.isVisible || link.isVisible(this.company())),
  );

  protected onStateChange(state: 'open' | 'closed'): void {
    this.state.set(state);
    this.openChange.emit(state === 'open');
  }

  protected close(): void {
    this.onStateChange('closed');
  }

  protected logout(): void {
    this.close();
    this.authService.logout();
  }
}
